import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { CredentialList } from "../components/CredentialList";
import { useAuth } from "../hooks/useAuth";
import { useIsMobile } from "../hooks/useIsMobile";
import { useT } from "../i18n/LanguageProvider";
import { listCredentials } from "../api/credentials";
import { enableShare, disableShare } from "../api/share";
import { uploadFile } from "../api/storage";

import {
  setProfessionalAvatar,
  removeProfessionalAvatar,
  updateProfessionalLocation,
  updateProfessionalSkills,
} from "../api/profile";
import { fetchProfessionalBilling } from "../api/billing";
import { downloadResume } from "../utils/resume";
import { formatDobToDmy } from "../utils/date";
import { getCanonicalProfileShareUrl } from "../utils/profileShare";
import { C, font, radii, shadows, space, type } from "../styles/tokens";
import { LiquidLoader } from "../components/LiquidLoader";
import { AadhaarVerification } from "../components/verification/AadhaarVerification";
import { ResumeUploadModal } from "../components/ResumeUploadModal";

/**
 * Verified Membership Tick Badge (using /membership-tick.png)
 * - Active: Vibrant green with green drop-shadow glow
 * - Expired: Neutral grey without glow
 */
function MembershipTickBadge({ active = false, size = 16, style = {} }) {
  return (
    <img
      src="/membership-tick.png"
      alt={active ? "Active Membership" : "Membership Expired"}
      title={active ? "Active Professional Membership" : "Membership Expired"}
      style={{
        width: size,
        height: size,
        display: "inline-block",
        verticalAlign: "middle",
        flexShrink: 0,
        transition: "all 0.3s ease",
        filter: active
          ? "drop-shadow(0 0 5px rgba(110, 204, 59, 0.85)) drop-shadow(0 0 10px rgba(72, 168, 45, 0.5))"
          : "grayscale(100%) opacity(0.65)",
        ...style,
      }}
    />
  );
}

/**
 * Profile page -- Restructured Professional Identity Dashboard.
 *
 * Implements strict 5-level card structure:
 *   Level 1: Card Header (Icon, Title, Subtitle, Header Action)
 *   Level 2: Primary Content (Full Name, Trust Score, Degree, Job Title)
 *   Level 3: Secondary Information (Contact info, Company, Duration, Grades)
 *   Level 4: Status Badges (Verified/Pending/Rejected)
 *   Level 5: Predictable Action Placement (Item Footers / Bottom Bar)
 */
export function Profile() {
  const t = useT();
  const isMobile = useIsMobile();
  const { localUser, refreshLocalUser } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const professional = localUser?.professional;
  const [membershipData, setMembershipData] = useState(null);

  useEffect(() => {
    if (localUser?.type === "PROFESSIONAL" || localUser?.professional) {
      fetchProfessionalBilling()
        .then((res) => setMembershipData(res?.membership))
        .catch(() => {});
    }
  }, [localUser?.type, localUser?.professional]);

  const isMembershipActive = useMemo(() => {
    const mem = membershipData || professional?.membership;
    if (mem) {
      const notExpired = mem.expiresAt
        ? new Date(mem.expiresAt).getTime() > Date.now()
        : true;
      return mem.status === "ACTIVE" && notExpired;
    }
    return !!professional && !professional.isSuspended;
  }, [membershipData, professional]);

  const [shareEnabled, setShareEnabled] = useState(
    professional?.shareEnabled ?? false,
  );
  const [shareToken, setShareToken] = useState(
    professional?.shareToken ?? null,
  );
  const [shareSaving, setShareSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    if (!sharePopupOpen) return undefined;
    function onPointer(e) {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setSharePopupOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [sharePopupOpen]);

  useEffect(() => {
    if (professional) {
      setShareEnabled(professional.shareEnabled ?? false);
      setShareToken(professional.shareToken ?? null);
    }
  }, [professional?.shareEnabled, professional?.shareToken]);

  // Location editing state
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [locationSuccess, setLocationSuccess] = useState(null);

  const handleOpenEditLocation = () => {
    setLocationError(null);
    setLocationSuccess(null);
    const existing = [professional?.city, professional?.state]
      .filter(Boolean)
      .join(", ");
    setLocationInput(existing);
    setIsEditingLocation(true);
  };

  const handleSaveLocation = async (e) => {
    if (e) e.preventDefault();
    setSavingLocation(true);
    setLocationError(null);
    try {
      await updateProfessionalLocation(locationInput);
      await refreshLocalUser();
      setIsEditingLocation(false);
      setLocationSuccess("Location updated successfully.");
      setTimeout(() => setLocationSuccess(null), 3000);
    } catch (err) {
      setLocationError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update location.",
      );
    } finally {
      setSavingLocation(false);
    }
  };

  // Skills editing state
  const skills = useMemo(
    () => professional?.skills ?? [],
    [professional?.skills],
  );
  const [isEditingSkills, setIsEditingSkills] = useState(false);
  const [skillsList, setSkillsList] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [savingSkills, setSavingSkills] = useState(false);
  const [skillsError, setSkillsError] = useState(null);
  const [skillsSuccess, setSkillsSuccess] = useState(null);

  const handleOpenEditSkills = () => {
    setSkillsList(professional?.skills ?? []);
    setSkillInput("");
    setSkillsError(null);
    setSkillsSuccess(null);
    setIsEditingSkills(true);
  };

  const handleAddSkill = () => {
    const trimmed = skillInput.trim();
    if (!trimmed) return;
    if (trimmed.length > 50) {
      setSkillsError("Each skill must be up to 50 characters.");
      return;
    }
    if (skillsList.length >= 30) {
      setSkillsError("You can list up to 30 skills.");
      return;
    }
    if (skillsList.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      setSkillsError(`"${trimmed}" is already in your skills list.`);
      return;
    }
    setSkillsList((prev) => [...prev, trimmed]);
    setSkillInput("");
    setSkillsError(null);
  };

  const handleRemoveSkill = (indexToRemove) => {
    setSkillsList((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    setSkillsError(null);
  };

  const handleSaveSkills = async () => {
    setSavingSkills(true);
    setSkillsError(null);
    setSkillsSuccess(null);
    try {
      let toSave = [...skillsList];
      const pending = skillInput.trim();
      if (
        pending &&
        pending.length <= 50 &&
        toSave.length < 30 &&
        !toSave.some((s) => s.toLowerCase() === pending.toLowerCase())
      ) {
        toSave.push(pending);
        setSkillInput("");
      }
      await updateProfessionalSkills(toSave);
      await refreshLocalUser();
      setIsEditingSkills(false);
      setSkillsSuccess("Skills updated successfully.");
      setTimeout(() => setSkillsSuccess(null), 3000);
    } catch (err) {
      setSkillsError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update skills.",
      );
    } finally {
      setSavingSkills(false);
    }
  };

  const fullName = useMemo(
    () =>
      professional
        ? `${professional.firstName} ${professional.lastName}`
        : localUser?.email,
    [professional, localUser],
  );

  async function reload() {
    try {
      if (localUser?.type === "PROFESSIONAL" || localUser?.professional) {
        const items = await listCredentials();
        setCredentials(items);
      }
      setError(null);
    } catch (err) {
      setError(err.message ?? t("common.somethingWrong"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, [localUser?.type, localUser?.professional]);

  // Handle deep-linking from notifications (?credential=credId)
  useEffect(() => {
    if (credentials.length === 0) return;
    const searchParams = new URLSearchParams(window.location.search);
    const credId = searchParams.get("credential");
    if (credId) {
      const el = document.getElementById(`credential-${credId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "box-shadow 0.5s ease";
        el.style.boxShadow = "0 0 0 2px #2563eb";
        setTimeout(() => {
          if (el) el.style.boxShadow = "";
        }, 2500);
      }
    }
  }, [credentials]);

  // Real backend verification statistics
  const stats = useMemo(() => {
    const total = credentials.length;
    const verified = credentials.filter((c) => c.status === "VERIFIED").length;
    const pending = credentials.filter((c) => c.status === "PENDING").length;
    const rejected = credentials.filter((c) => c.status === "REJECTED").length;
    const unverified = total - verified - pending - rejected;
    return { total, verified, pending, rejected, unverified };
  }, [credentials]);

  const responsiveCardStyle = useMemo(
    () => ({
      ...cardStyle,
      padding: isMobile ? 16 : 24,
    }),
    [isMobile],
  );

  const verifiedPct =
    stats.total > 0 ? Math.round((stats.verified / stats.total) * 100) : 0;

  const shareLink = getCanonicalProfileShareUrl(shareToken);

  async function copyShareLink() {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* fallback */
    }
  }

  async function handleNativeShare() {
    if (!shareLink) return;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "My verified profile on TickAndPass",
          text: `My verified TickAndPass profile: ${shareLink}`,
          url: shareLink,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    copyShareLink();
  }

  function handleWhatsAppShare() {
    if (!shareLink) return;
    const msg = encodeURIComponent(
      `My verified TickAndPass profile: ${shareLink}`,
    );
    window.open(
      `https://api.whatsapp.com/send?text=${msg}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  async function handleShareToggle(next) {
    setShareEnabled(next);
    setShareSaving(true);
    try {
      const result = next ? await enableShare() : await disableShare();
      setShareEnabled(result.shareEnabled);
      setShareToken(result.shareToken);
      await refreshLocalUser();
    } catch (err) {
      setShareEnabled(!next);
      setError(err.message ?? t("common.somethingWrong"));
    } finally {
      setShareSaving(false);
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <LiquidLoader text="Loading Your Profile Dashboard..." />
        </div>
      </AppShell>
    );
  }

  const isProfessional = localUser?.type === "PROFESSIONAL";
  const isAadhaarVerified = !!(
    professional?.aadhaarNumber ||
    localUser?.isVerified ||
    localUser?.aadhaarVerification ||
    localUser?.identities?.some(
      (i) => i.type === "AADHAAR" && i.status === "VERIFIED",
    )
  );

  return (
    <AppShell>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* -- 0. Aadhaar DigiLocker Onboarding Banner (Post-Auth Onboarding Flow) -- */}
        {isProfessional && !isAadhaarVerified && (
          <div
            style={{
              marginBottom: space.xl,
              background: C.surface,
              borderRadius: radii.card,
              padding: isMobile ? space.lg : space.xl,
              boxShadow: shadows.card,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                gap: space.md,
                marginBottom: space.md,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: C.infoTint,
                  color: C.navy,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ShieldCheckIcon color={C.navy} />
              </div>
              <div>
                <h3
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: C.navy,
                    margin: 0,
                  }}
                >
                  Complete Identity Verification (Aadhaar DigiLocker eKYC)
                </h3>
                <p
                  style={{ fontSize: 13, color: C.textMuted, margin: "2px 0 0" }}
                >
                  Authenticate your identity to earn a verified professional
                  badge and enable credential verification sharing.
                </p>
              </div>
            </div>
            <AadhaarVerification onVerificationSuccess={refreshLocalUser} />
          </div>
        )}

        {/* -- 1. Profile / Name Identity Card -- */}
        <section className="profile-section-card" style={responsiveCardStyle}>
          {/* Level 1: Card Header */}
          <div style={cardHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <UserIcon color={C.primary} />
              <div>
                <h2 style={cardTitleStyle}>PROFILE IDENTITY</h2>
                <div style={cardSubtitleStyle}>
                  Personal details & verification status
                </div>
              </div>
            </div>
          </div>

          {/* Level 2: Primary Profile Header */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 20,
              alignItems: "flex-start",
              marginBottom: 20,
            }}
          >
            <AvatarUploader
              photoUrl={professional?.avatarDownloadUrl}
              name={fullName}
              size={isMobile ? 64 : 80}
              onChanged={refreshLocalUser}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 4,
                }}
              >
                <h1
                  style={{
                    fontSize: isMobile ? 20 : 24,
                    fontWeight: 800,
                    color: C.navy,
                    margin: 0,
                    letterSpacing: "-0.4px",
                    lineHeight: 1.2,
                  }}
                >
                  {fullName}
                </h1>

                {/* Role Badge */}
                {professional && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.primary,
                      background: C.primaryTint,
                      border: "none",
                      padding: "4px 12px",
                      borderRadius: radii.pill,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    <MembershipTickBadge
                      active={isMembershipActive}
                      size={15}
                    />
                    {professional.isStudent
                      ? t("profile.student")
                      : t("profile.professional")}
                  </span>
                )}

                {/* Community Tag */}
                {professional?.community && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.green,
                      background: C.greenLight,
                      padding: "2px 10px",
                      borderRadius: 12,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {professional.community}
                  </span>
                )}
              </div>

              <div
                style={{ fontSize: 13.5, color: C.textMed, fontWeight: 500 }}
              >
                {professional?.isStudent
                  ? "Student Identity"
                  : "Verified Professional Account"}
              </div>
            </div>
          </div>

          <div
            style={{
              borderTop: "1px solid #e2e8f0",
              paddingTop: 16,
              marginBottom: 16,
            }}
          >
            {/* Level 3: Grouped Contact Information */}
            {/* Level 3: Grouped Contact Information */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                gap: 16,
              }}
            >
              {/* Email Group */}
              <div style={infoGroupStyle}>
                <div style={infoLabelStyle}>Email Address</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={infoValueStyle}>{localUser?.email}</span>
                  {localUser?.email ? (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.green,
                        background: C.greenLight,
                        padding: "1px 6px",
                        borderRadius: 8,
                      }}
                    >
                      Verified
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.amber,
                        background: C.amberLight,
                        padding: "1px 6px",
                        borderRadius: 8,
                      }}
                    >
                      Unverified
                    </span>
                  )}
                </div>
              </div>

              {/* Date of Birth Group (from Aadhaar) */}
              <div style={infoGroupStyle}>
                <div style={infoLabelStyle}>Date of Birth</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={infoValueStyle}>
                    {formatDobToDmy(
                      localUser?.aadhaarVerification?.dob ||
                        localUser?.aadhaarVerification?.dateOfBirth ||
                        localUser?.dob ||
                        localUser?.professional?.dob,
                    ) || "Not Provided"}
                  </span>
                  {localUser?.aadhaarVerification?.dob && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.green,
                        background: C.greenLight,
                        padding: "1px 6px",
                        borderRadius: 8,
                      }}
                    >
                      Aadhaar
                    </span>
                  )}
                </div>
              </div>

              {/* Location Group */}
              <div style={infoGroupStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={infoLabelStyle}>Location</div>
                  {!isEditingLocation && (
                    <button
                      type="button"
                      onClick={handleOpenEditLocation}
                      style={{
                        width: 17,
                        height: 17,
                        borderRadius: "50%",
                        background: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        lineHeight: 1,
                        padding: 0,
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#eff6ff";
                        e.currentTarget.style.color = "#2563eb";
                        e.currentTarget.style.borderColor = "#93c5fd";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#f1f5f9";
                        e.currentTarget.style.color = "#475569";
                        e.currentTarget.style.borderColor = "#cbd5e1";
                      }}
                      title="Add or update location"
                    >
                      +
                    </button>
                  )}
                </div>

                {isEditingLocation ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                      marginTop: 2,
                    }}
                  >
                    <input
                      type="text"
                      value={locationInput}
                      onChange={(e) => setLocationInput(e.target.value)}
                      placeholder="City, State"
                      autoFocus
                      style={{
                        padding: "4px 8px",
                        fontSize: 12,
                        borderRadius: 6,
                        border: "1px solid #3b82f6",
                        outline: "none",
                        fontFamily: font,
                        minWidth: 140,
                        maxWidth: 220,
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveLocation(e);
                        if (e.key === "Escape") setIsEditingLocation(false);
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSaveLocation}
                      disabled={savingLocation}
                      style={{
                        padding: "4px 10px",
                        fontSize: 11.5,
                        fontWeight: 700,
                        borderRadius: 6,
                        background: "#2563eb",
                        color: "#ffffff",
                        border: "none",
                        cursor: savingLocation ? "not-allowed" : "pointer",
                        fontFamily: font,
                        opacity: savingLocation ? 0.6 : 1,
                      }}
                    >
                      {savingLocation ? "..." : "Update"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingLocation(false)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#64748b",
                        fontSize: 12,
                        cursor: "pointer",
                        padding: "2px 4px",
                      }}
                      title="Cancel"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={infoValueStyle}>
                    {professional?.city
                      ? `${professional.city}${professional.state ? `, ${professional.state}` : ""}${professional?.pincode ? ` (${professional.pincode})` : ""}`
                      : "Location Not Set"}
                  </div>
                )}

                {locationError && (
                  <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>
                    {locationError}
                  </div>
                )}
                {locationSuccess && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#16a34a",
                      fontWeight: 600,
                      marginTop: 2,
                    }}
                  >
                    {locationSuccess}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Level 4 & 5: Profile Status & Actions Bar */}
          <div
            style={{
              borderTop: "1px solid #e2e8f0",
              paddingTop: 14,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 12,
            }}
          >
            {/* Level 4: Status Indicator */}
            {/* <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: C.green,
                }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
                Profile Active
              </span>
            </div> */}
            {/* Level 4: Status Indicator & Annual Subscription */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: C.green,
                  }}
                />
                <span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
                  Profile Active
                </span>
              </div>
            </div>

            {/* Level 5: Actions */}
            <div
              ref={shareRef}
              style={{
                position: "relative",
                display: "flex",
                gap: 10,
                alignItems: "center",
                width: isMobile ? "100%" : "auto",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              <button
                type="button"
                onClick={() => setSharePopupOpen((v) => !v)}
                style={{
                  ...actionBtnStyle,
                  flex: isMobile ? 1 : "initial",
                  justifyContent: "center",
                  minHeight: isMobile ? 44 : undefined,
                  background: shareEnabled ? "#eff6ff" : "white",
                  color: "#2563eb",
                  borderColor: shareEnabled ? "#2563eb" : "#cbd5e1",
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                <span>Share Profile</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  downloadResume({
                    name: fullName,
                    role: professional
                      ? professional.isStudent
                        ? t("profile.student")
                        : t("profile.professional")
                      : "",
                    location: professional?.city
                      ? professional.city +
                        (professional.state ? `, ${professional.state}` : "")
                      : "",
                    email: localUser?.email,
                    bio: professional?.bio,
                    credentials,
                  })
                }
                disabled={false}
                style={{
                  ...actionBtnStyle,
                  flex: isMobile ? 1 : "initial",
                  justifyContent: "center",
                  minHeight: isMobile ? 44 : undefined,
                  background: "#057642",
                  color: "#ffffff",
                  borderColor: "#057642",
                  cursor: "pointer",
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Export PDF</span>
              </button>


              {sharePopupOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 10px)",
                    right: isMobile ? "auto" : 0,
                    left: isMobile ? 0 : "auto",
                    zIndex: 100,
                    width: isMobile ? "min(320px, calc(100vw - 48px))" : 320,
                    maxWidth: "calc(100vw - 32px)",
                    background: "#ffffff",
                    border: `1px solid ${C.divider}`,
                    borderRadius: 10,
                    padding: 16,
                    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.15)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{ fontSize: 14, fontWeight: 700, color: C.navy }}
                    >
                      {t("profile.shareMyProfile")}
                    </div>
                    <Toggle
                      on={shareEnabled}
                      onChange={handleShareToggle}
                      disabled={shareSaving}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.textMed,
                      marginBottom: 12,
                      lineHeight: 1.45,
                    }}
                  >
                    {shareEnabled
                      ? "Your verified profile is active for registered verifiers."
                      : "Enable sharing to get a secure link for employers and institutions."}
                  </div>
                  {shareEnabled && shareLink && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "8px 10px",
                          background: "#f8fafc",
                          borderRadius: 6,
                          border: `1px solid ${C.divider}`,
                        }}
                      >
                        <code
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 11,
                            color: C.navy,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontFamily: "monospace",
                            fontWeight: 600,
                          }}
                        >
                          {shareLink}
                        </code>
                        <button
                          type="button"
                          onClick={copyShareLink}
                          style={{
                            padding: "4px 10px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: linkCopied ? C.green : "#2563eb",
                            background: linkCopied ? C.greenLight : "#eff6ff",
                            border: `1px solid ${linkCopied ? C.green : "#bfdbfe"}`,
                            borderRadius: 4,
                            cursor: "pointer",
                            fontFamily: font,
                            flexShrink: 0,
                          }}
                        >
                          {linkCopied ? "Copied!" : "Copy Link"}
                        </button>
                      </div>

                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          onClick={handleWhatsAppShare}
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            padding: "7px 12px",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#ffffff",
                            background: "#25D366",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontFamily: font,
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.711 2.598 2.664-.698c.969.586 1.777.947 2.801.947 3.183 0 5.768-2.586 5.769-5.766.001-3.182-2.585-5.768-5.77-5.768zm0-2.172c4.385 0 7.944 3.559 7.944 7.939 0 4.381-3.559 7.94-7.944 7.94-1.34 0-2.6-.334-3.712-.924l-4.319 1.132 1.155-4.218c-.688-1.189-1.068-2.536-1.068-3.93 0-4.38 3.559-7.939 7.944-7.939z" />
                          </svg>
                          <span>WhatsApp</span>
                        </button>

                        {typeof navigator !== "undefined" &&
                          typeof navigator.share === "function" && (
                            <button
                              type="button"
                              onClick={handleNativeShare}
                              style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 6,
                                padding: "7px 12px",
                                fontSize: 12,
                                fontWeight: 600,
                                color: C.navy,
                                background: "#f1f5f9",
                                border: `1px solid ${C.divider}`,
                                borderRadius: 6,
                                cursor: "pointer",
                                fontFamily: font,
                              }}
                            >
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <circle cx="18" cy="5" r="3" />
                                <circle cx="6" cy="12" r="3" />
                                <circle cx="18" cy="19" r="3" />
                                <line
                                  x1="8.59"
                                  y1="13.51"
                                  x2="15.42"
                                  y2="17.49"
                                />
                                <line
                                  x1="15.41"
                                  y1="6.51"
                                  x2="8.59"
                                  y2="10.49"
                                />
                              </svg>
                              <span>Share...</span>
                            </button>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* -- Fast-Track Resume Onboarding Banner (Professional Portal Only) -- */}
        {isProfessional && (
          <div
            style={{
              background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)",
              border: "1.5px solid #bbf7d0",
              borderRadius: 12,
              padding: isMobile ? 16 : 20,
              display: "flex",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: "space-between",
              flexDirection: isMobile ? "column" : "row",
              gap: 16,
              boxShadow: "0 4px 16px rgba(5, 118, 66, 0.06)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "linear-gradient(135deg, #057642 0%, #08833E 100%)",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  flexShrink: 0,
                  boxShadow: "0 4px 12px rgba(5, 118, 66, 0.25)",
                }}
              >
                ⚡
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>
                  Fast-Track Your Profile with Resume Upload
                </div>
                <div style={{ fontSize: 13, color: C.textMed, marginTop: 2 }}>
                  Upload a PDF, Word document, or capture a photo with your camera. We'll automatically extract your work history, education, skills, and summary for review before saving.
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setResumeModalOpen(true)}
              style={{
                padding: "10px 20px",
                fontSize: 13.5,
                fontWeight: 700,
                color: "#ffffff",
                background: "#057642",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 4px 12px rgba(5, 118, 66, 0.2)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span>Auto-Fill from Resume</span>
              <span style={{ fontSize: 16 }}>→</span>
            </button>
          </div>
        )}

        {/* -- 2. Horizontal Cards Row (Desktop: 3 Columns, Mobile: Stacked) -- */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: 20,
            alignItems: "stretch",
          }}
        >
          {/* -- Card A: Verified Credentials -- */}
          <section
            className="profile-section-card"
            style={{
              ...responsiveCardStyle,
              display: "flex",
              flexDirection: "column",
              height: isMobile ? "auto" : 500,
            }}
          >
            {/* Level 1: Card Header */}
            <div style={cardHeaderStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <ShieldCheckIcon color={C.green} />
                <div>
                  <h2 style={cardTitleStyle}>VERIFIED CREDENTIALS</h2>
                  <div style={cardSubtitleStyle}>
                    Trust & verification status
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable / Flexible Content Area */}
            <div
              className="profile-card-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                paddingRight: 4,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Level 2: Primary Status Summary */}
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: C.navy,
                    marginBottom: 2,
                  }}
                >
                  {stats.verified} Credentials Verified
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: C.textMed,
                    lineHeight: 1.45,
                    marginBottom: 10,
                  }}
                >
                  Your submitted credentials have been verified by issuing
                  institutions.
                </div>
                <ProgressBarWithLabel
                  verified={stats.verified}
                  pending={stats.pending}
                  rejected={stats.rejected}
                  total={stats.total}
                  verifiedPct={verifiedPct}
                />
              </div>

              {/* Level 3 & 4: Structured Trust Checklist */}
              <div
                style={{
                  borderTop: `1px solid ${C.divider}`,
                  paddingTop: 14,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <ChecklistItem
                  label="Aadhaar DigiLocker eKYC"
                  status={
                    professional?.aadhaarNumber ||
                    localUser?.identities?.some(
                      (i) => i.type === "AADHAAR" && i.status === "VERIFIED",
                    )
                      ? "VERIFIED"
                      : "VERIFIED"
                  }
                  subtitle={
                    professional?.aadhaarNumber
                      ? `Verified Aadhaar (${professional.aadhaarNumber})`
                      : "Identity authenticated via Cashfree DigiLocker"
                  }
                />
                <ChecklistItem
                  label="Email Verification"
                  status={localUser?.email ? "VERIFIED" : "UNVERIFIED"}
                  subtitle={
                    localUser?.email ? "Email verified" : "Verification pending"
                  }
                />
                <ChecklistItem
                  label="Academic Credentials"
                  status={
                    credentials.some(
                      (c) => c.type === "EDUCATION" && c.status === "VERIFIED",
                    )
                      ? "VERIFIED"
                      : credentials.some(
                            (c) =>
                              c.type === "EDUCATION" && c.status === "PENDING",
                          )
                        ? "PENDING"
                        : "NONE"
                  }
                  subtitle={`${credentials.filter((c) => c.type === "EDUCATION" && c.status === "VERIFIED").length} degrees verified`}
                />
                <ChecklistItem
                  label="Work History"
                  status={
                    credentials.some(
                      (c) => c.type === "WORK" && c.status === "VERIFIED",
                    )
                      ? "VERIFIED"
                      : credentials.some(
                            (c) => c.type === "WORK" && c.status === "PENDING",
                          )
                        ? "PENDING"
                        : "NONE"
                  }
                  subtitle={`${credentials.filter((c) => c.type === "WORK" && c.status === "VERIFIED").length} positions verified`}
                />
              </div>
            </div>

            {/* Level 5: Card Action Footer */}
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: `1px solid ${C.divider}`,
                textAlign: "right",
                flexShrink: 0,
              }}
            >
              <Link to="/verifications" style={cardHeaderLinkStyle}>
                View Verification Status
              </Link>
            </div>
          </section>

          {/* -- Card B: My Education -- */}
          <section
            className="profile-section-card"
            style={{
              ...responsiveCardStyle,
              display: "flex",
              flexDirection: "column",
              height: isMobile ? "auto" : 500,
            }}
          >
            {/* Level 1: Card Header */}
            <div style={cardHeaderStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <AcademicCapIcon color="#2563eb" />
                <div>
                  <h2 style={cardTitleStyle}>MY EDUCATION</h2>
                  <div style={cardSubtitleStyle}>Academic background</div>
                </div>
              </div>
              <Link to="/credentials/new/education" style={addBtnStyle}>
                + Add Education
              </Link>
            </div>

            {/* Education List Container */}
            <div
              className="profile-card-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                paddingRight: 4,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CredentialList
                type="EDUCATION"
                credentials={credentials}
                onChanged={reload}
                emptyHint="No education details added yet."
                addLink="/credentials/new/education"
                editLinkPrefix="/credentials/edit/education"
              />
            </div>
          </section>

          {/* -- Card C: Work Experience -- */}
          <section
            className="profile-section-card"
            style={{
              ...responsiveCardStyle,
              display: "flex",
              flexDirection: "column",
              height: isMobile ? "auto" : 500,
            }}
          >
            {/* Level 1: Card Header */}
            <div style={cardHeaderStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <BriefcaseIcon color="#2563eb" />
                <div>
                  <h2 style={cardTitleStyle}>WORK EXPERIENCE</h2>
                  <div style={cardSubtitleStyle}>
                    Professional experience & employment timeline
                  </div>
                </div>
              </div>
              <Link to="/credentials/new/work" style={addBtnStyle}>
                + Add Experience
              </Link>
            </div>

            {/* Work Experience Timeline */}
            <div
              className="profile-card-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                paddingRight: 4,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CredentialList
                type="WORK"
                credentials={credentials}
                onChanged={reload}
                emptyHint="No work experience details added yet."
                addLink="/credentials/new/work"
                editLinkPrefix="/credentials/edit/work"
              />
            </div>
          </section>
        </div>

        {/* -- Skills Card (Full Width Section) -- */}
        <section className="profile-section-card" style={responsiveCardStyle}>
          {/* Level 1: Card Header */}
          <div style={cardHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <SparklesIcon color="#2563eb" />
              <div>
                <h2 style={cardTitleStyle}>SKILLS</h2>
                <div style={cardSubtitleStyle}>
                  Key competencies & skills (up to 30)
                </div>
              </div>
            </div>
            {!isEditingSkills ? (
              <button
                type="button"
                onClick={handleOpenEditSkills}
                style={{ ...addBtnStyle, cursor: "pointer" }}
              >
                {skills.length > 0 ? "Edit Skills" : "+ Add Skills"}
              </button>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={handleSaveSkills}
                  disabled={savingSkills}
                  style={{
                    padding: "5px 14px",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#ffffff",
                    background: "#2563eb",
                    border: "none",
                    borderRadius: 6,
                    cursor: savingSkills ? "not-allowed" : "pointer",
                    fontFamily: font,
                    opacity: savingSkills ? 0.7 : 1,
                  }}
                >
                  {savingSkills ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditingSkills(false)}
                  disabled={savingSkills}
                  style={{
                    padding: "5px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#64748b",
                    background: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontFamily: font,
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {skillsError && (
            <div
              style={{
                fontSize: 12,
                color: "#dc2626",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                padding: "8px 12px",
                borderRadius: 6,
                marginBottom: 12,
              }}
            >
              {skillsError}
            </div>
          )}

          {skillsSuccess && (
            <div
              style={{
                fontSize: 12,
                color: "#16a34a",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                padding: "8px 12px",
                borderRadius: 6,
                marginBottom: 12,
              }}
            >
              {skillsSuccess}
            </div>
          )}

          {isEditingSkills ? (
            <div>
              {/* Tag Input Field */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginBottom: 12,
                }}
              >
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => {
                    setSkillInput(e.target.value);
                    if (skillsError) setSkillsError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                  maxLength={50}
                  disabled={skillsList.length >= 30 || savingSkills}
                  placeholder={
                    skillsList.length >= 30
                      ? "Maximum 30 skills reached"
                      : "Type a skill and press Enter or click Add (up to 50 chars)"
                  }
                  style={{
                    flex: 1,
                    minWidth: 220,
                    padding: "8px 12px",
                    fontSize: 13,
                    borderRadius: 6,
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    fontFamily: font,
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddSkill}
                  disabled={
                    !skillInput.trim() ||
                    skillsList.length >= 30 ||
                    savingSkills
                  }
                  style={{
                    padding: "8px 16px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: "#2563eb",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 6,
                    cursor:
                      !skillInput.trim() ||
                      skillsList.length >= 30 ||
                      savingSkills
                        ? "not-allowed"
                        : "pointer",
                    fontFamily: font,
                    opacity:
                      !skillInput.trim() ||
                      skillsList.length >= 30 ||
                      savingSkills
                        ? 0.5
                        : 1,
                  }}
                >
                  Add Skill
                </button>
              </div>

              {/* Skills count and hint */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: 12,
                  color: C.textMed,
                  marginBottom: 14,
                }}
              >
                <span>Press Enter or comma to add each skill</span>
                <span
                  style={{
                    fontWeight: 700,
                    color: skillsList.length >= 30 ? "#dc2626" : C.textDark,
                  }}
                >
                  {skillsList.length} / 30 skills
                </span>
              </div>

              {/* Editable chips */}
              {skillsList.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {skillsList.map((skill, index) => (
                    <span
                      key={index}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "#1e40af",
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: 16,
                        padding: "4px 10px 4px 12px",
                        lineHeight: 1.3,
                      }}
                    >
                      <span>{skill}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSkill(index)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#3b82f6",
                          cursor: "pointer",
                          fontSize: 14,
                          fontWeight: 700,
                          lineHeight: 1,
                          padding: "0 2px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        title={`Remove ${skill}`}
                        aria-label={`Remove ${skill}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    padding: "16px",
                    background: "#f8fafc",
                    borderRadius: 8,
                    textAlign: "center",
                    fontSize: 12.5,
                    color: C.textLight,
                  }}
                >
                  No skills in list. Type above and click Add Skill to begin.
                </div>
              )}
            </div>
          ) : (
            <div>
              {skills.length > 0 ? (
                <>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {skills.map((skill, index) => (
                      <span
                        key={index}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "#1e40af",
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 16,
                          padding: "4px 12px",
                          lineHeight: 1.3,
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      fontSize: 11.5,
                      color: C.textLight,
                      fontWeight: 500,
                    }}
                  >
                    {skills.length} of 30 skills listed
                  </div>
                </>
              ) : (
                <div
                  style={{
                    padding: "20px 16px",
                    background: "#f8fafc",
                    borderRadius: 8,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: C.textMed,
                      marginBottom: 8,
                    }}
                  >
                    No skills listed yet. Add up to 30 skills to showcase your
                    capabilities to verifiers and employers.
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenEditSkills}
                    style={{
                      ...addBtnStyle,
                      cursor: "pointer",
                      display: "inline-flex",
                    }}
                  >
                    + Add Skills
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* -- 3. Others Card (Full Width Rectangular Bottom Section) -- */}
        <section className="profile-section-card" style={responsiveCardStyle}>
          {/* Level 1: Card Header */}
          <div style={cardHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <AwardBadgeIcon color="#2563eb" />
              <div>
                <h2 style={cardTitleStyle}>OTHERS</h2>
                <div style={cardSubtitleStyle}>Certifications & licenses</div>
              </div>
            </div>
            <Link to="/credentials/new/other" style={addBtnStyle}>
              + Add Credential
            </Link>
          </div>

          {/* Others List */}
          <CredentialList
            type="OTHER"
            credentials={credentials}
            onChanged={reload}
            emptyHint="No additional credentials added yet."
            addLink="/credentials/new/other"
            editLinkPrefix="/credentials/edit/other"
          />
        </section>

        {error && (
          <div
            style={{
              background: C.redLight,
              color: C.red,
              border: `1px solid ${C.red}`,
              padding: "12px 16px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}
        {/* Resume Upload & Review Modal */}
        <ResumeUploadModal
          isOpen={resumeModalOpen}
          onClose={() => setResumeModalOpen(false)}
          onApplied={async () => {
            await reload();
            await refreshLocalUser();
          }}
          currentProfile={professional}
        />
      </div>
    </AppShell>
  );
}

// =============================================================================
// Helper Components & Styling
// =============================================================================

function ChecklistItem({ label, status, subtitle }) {
  const isVerified = status === "VERIFIED";
  const isPending = status === "PENDING";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: 13,
      }}
    >
      <div>
        <div style={{ fontWeight: 600, color: C.navy }}>{label}</div>
        <div style={{ fontSize: 11, color: C.textMed }}>{subtitle}</div>
      </div>

      {/* Verification Tick Mark Logo: Green if verified, Grey if not verified */}
      <img
        src="/verified-tick.png"
        alt={isVerified ? "Verified" : isPending ? "Pending" : "Not Verified"}
        title={isVerified ? "Verified" : isPending ? "Pending" : "Not Verified"}
        style={{
          width: 22,
          height: 22,
          display: "block",
          flexShrink: 0,
          transition: "all 0.2s ease",
          filter: isVerified
            ? "drop-shadow(0 1px 3px rgba(116, 204, 56, 0.45))"
            : "grayscale(100%) opacity(0.55)",
        }}
      />
    </div>
  );
}

function ProgressBarWithLabel({ verified, pending, rejected, total }) {
  if (total === 0) {
    return (
      <div
        style={{
          height: 8,
          background: "#e2e8f0",
          borderRadius: 4,
          overflow: "hidden",
        }}
      />
    );
  }
  const verifiedW = (verified / total) * 100;
  const pendingW = (pending / total) * 100;
  const rejectedW = (rejected / total) * 100;
  return (
    <div>
      <div
        style={{
          height: 10,
          background: "#e2e8f0",
          borderRadius: 5,
          overflow: "hidden",
          display: "flex",
        }}
      >
        {verifiedW > 0 && (
          <div style={{ width: `${verifiedW}%`, background: C.green }} />
        )}
        {pendingW > 0 && (
          <div style={{ width: `${pendingW}%`, background: C.amber }} />
        )}
        {rejectedW > 0 && (
          <div style={{ width: `${rejectedW}%`, background: C.red }} />
        )}
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          marginTop: 8,
          fontSize: 11,
          color: C.textMed,
          flexWrap: "wrap",
        }}
      >
        <Legend color={C.green} label={`${verified} Verified`} />
        {pending > 0 && <Legend color={C.amber} label={`${pending} Pending`} />}
        {rejected > 0 && (
          <Legend color={C.red} label={`${rejected} Rejected`} />
        )}
        {total - verified - pending - rejected > 0 && (
          <Legend
            color={C.textLight}
            label={`${total - verified - pending - rejected} Unverified`}
          />
        )}
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontWeight: 600,
      }}
    >
      <span
        style={{ width: 7, height: 7, borderRadius: "50%", background: color }}
      />
      {label}
    </span>
  );
}

function AvatarUploader({ photoUrl, name = "", size = 80, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const displayUrl = previewUrl || photoUrl;
  const initials = name
    .split(" ")
    .map((s) => s.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Please choose a valid PNG or JPG image.");
      return;
    }
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
    setBusy(true);
    setErr(null);
    try {
      const { s3Key } = await uploadFile(file, "avatar");
      await setProfessionalAvatar(s3Key);
      await onChanged?.();
    } catch (e2) {
      setErr(e2.message ?? "Upload failed.");
      setPreviewUrl(null);
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    setBusy(true);
    setErr(null);
    try {
      await removeProfessionalAvatar();
      setPreviewUrl(null);
      await onChanged?.();
    } catch (e2) {
      setErr(e2.message ?? "Couldn't remove photo.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <div
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: "#eff6ff",
            color: "#2563eb",
            border: `2px solid #bfdbfe`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: size * 0.36,
            fontWeight: 800,
            letterSpacing: -0.5,
            overflow: "hidden",
          }}
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            initials || "?"
          )}
        </div>

        <button
          type="button"
          onClick={() => !busy && inputRef.current?.click()}
          disabled={busy}
          title={displayUrl ? "Change photo" : "Add a profile photo"}
          aria-label={displayUrl ? "Change photo" : "Add a profile photo"}
          style={{
            position: "absolute",
            right: -2,
            bottom: -2,
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "#2563eb",
            color: "white",
            border: `2px solid #ffffff`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: busy ? "default" : "pointer",
            padding: 0,
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        </button>

        {displayUrl && !busy && (
          <button
            type="button"
            onClick={onRemove}
            title="Remove photo"
            aria-label="Remove photo"
            style={{
              position: "absolute",
              right: -2,
              top: -2,
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "#ffffff",
              color: C.red,
              border: `1px solid ${C.dividerStrong}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}

        {busy && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 700,
              color: C.textDark,
            }}
          >
            ...
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          onChange={onPick}
          style={{ display: "none" }}
        />
      </div>
      {err && (
        <div
          style={{
            fontSize: 11,
            color: C.red,
            marginTop: 4,
            maxWidth: size + 40,
          }}
        >
          {err}
        </div>
      )}
    </div>
  );
}

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        border: 0,
        background: on ? C.green : "#cbd5e1",
        position: "relative",
        cursor: disabled ? "default" : "pointer",
        transition: "background 0.18s ease",
        flexShrink: 0,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 2,
          left: on ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          transition: "left 0.18s ease",
        }}
      />
    </button>
  );
}

// Icons
function UserIcon({ color = "#2563eb" }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function ShieldCheckIcon({ color = C.green }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function AcademicCapIcon({ color = C.primary }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  );
}

function AwardBadgeIcon({ color = C.primary }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="8" r="6" />
      <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  );
}

function BriefcaseIcon({ color = C.primary }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function SparklesIcon({ color = C.primary }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2l2.4 7.2 7.6.8-5.8 5.2 1.8 7.4-6-3.8-6 3.8 1.8-7.4-5.8-5.2 7.6-.8z" />
    </svg>
  );
}

// Styling Constants
const cardStyle = {
  backgroundColor: C.surface,
  borderRadius: radii.card,
  padding: 24,
  boxShadow: shadows.card,
};

const cardHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 10,
  minHeight: 46,
  flexShrink: 0,
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: `1px solid ${C.divider}`,
};

const cardTitleStyle = {
  fontSize: 15,
  fontWeight: 700,
  color: C.navy,
  margin: 0,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
};

const cardSubtitleStyle = {
  fontSize: 12,
  color: C.textMuted,
  marginTop: 2,
};

const cardHeaderLinkStyle = {
  fontSize: 12.5,
  fontWeight: 600,
  color: C.primary,
  textDecoration: "none",
};

const infoGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const infoLabelStyle = {
  fontSize: 11.5,
  fontWeight: 600,
  color: C.textSubtle,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
};

const infoValueStyle = {
  fontSize: 13.5,
  fontWeight: 600,
  color: C.navy,
};

const addBtnStyle = {
  padding: "4px 12px",
  fontSize: 12,
  fontWeight: 600,
  color: C.primary,
  background: C.primaryTint,
  border: "none",
  borderRadius: radii.pill,
  textDecoration: "none",
  fontFamily: font,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};

const actionBtnStyle = {
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 700,
  borderRadius: 8,
  border: "1px solid",
  cursor: "pointer",
  fontFamily: font,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  transition: "all 0.15s ease",
};
